import { useEffect, useMemo, useRef, useState, type FocusEvent } from "react";
import type { Feature, FeatureCollection, MultiPolygon, Polygon } from "geojson";
import type { BuildingProperties } from "../map/layers";

interface Hit {
  feature: Feature<Polygon | MultiPolygon, BuildingProperties>;
  display: string;
  alt?: string;
  centroid: [number, number];
}

interface Props {
  buildings: FeatureCollection<Polygon | MultiPolygon, BuildingProperties> | null;
  onSelect: (centroid: [number, number], building: BuildingProperties) => void;
}

function centroid(geom: Polygon | MultiPolygon): [number, number] {
  const ring =
    geom.type === "Polygon"
      ? geom.coordinates[0]
      : geom.coordinates[0][0]; // first ring of first polygon for MultiPolygon
  let sx = 0;
  let sy = 0;
  for (const [x, y] of ring) {
    sx += x;
    sy += y;
  }
  return [sx / ring.length, sy / ring.length];
}

export function BuildingSearch({ buildings, onSelect }: Props) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const hostRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    // 350 ms (was 150) gives the user time to move the cursor from the input
    // onto a dropdown row without the list vanishing mid-click.
    timeoutRef.current = window.setTimeout(() => setOpen(false), 350);
  };

  const index: Hit[] = useMemo(() => {
    if (!buildings) return [];
    const out: Hit[] = [];
    for (const f of buildings.features) {
      const p = f.properties;
      const en = p.nameEn ?? null;
      const th = p.nameTh ?? null;
      const generic = p.name ?? null;
      const display = en ?? generic ?? th;
      if (!display) continue;
      const alt = display === th ? en ?? undefined : th ?? undefined;
      out.push({
        feature: f,
        display,
        alt,
        centroid: centroid(f.geometry),
      });
    }
    return out;
  }, [buildings]);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [] as Hit[];
    return index
      .filter((h) => {
        return (
          h.display.toLowerCase().includes(needle) ||
          (h.alt && h.alt.toLowerCase().includes(needle))
        );
      })
      .slice(0, 8);
  }, [index, q]);

  const pick = (h: Hit) => {
    onSelect(h.centroid, h.feature.properties);
    setQ(h.display);
    setOpen(false);
  };

  const inputId = "building-search-input";

  return (
    <div ref={hostRef} className="building-search" onBlur={handleBlur}>
      <label htmlFor={inputId} className="sr-only">Search campus buildings</label>
      <input
        ref={inputRef}
        id={inputId}
        className="building-search-input"
        type="search"
        aria-label="Search campus buildings"
        placeholder={
          buildings
            ? `Search ${index.length} buildings — try "engineering" or "วิศวกรรม"`
            : "Loading buildings…"
        }
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && open && results.length) {
            event.preventDefault(); hostRef.current?.querySelector<HTMLElement>('[role="option"]')?.focus();
          } else if (event.key === 'Escape' && open) {
            event.preventDefault(); event.stopPropagation(); setOpen(false);
          }
        }}
        onFocus={() => setOpen(true)}
        spellCheck={false}
        autoComplete="off"
      />
      {open && q.trim() !== "" && results.length === 0 && buildings && buildings.features.length > 0 && (
        <ul className="building-search-results" role="listbox" aria-live="polite">
          <li className="building-search-empty caption">No building matches &ldquo;{q.trim()}&rdquo; — try the Thai or English name.</li>
        </ul>
      )}
      {open && results.length > 0 && (
        <ul className="building-search-results" role="listbox">
          {results.map((h) => (
            <li
              key={h.feature.properties.id ?? `${h.centroid[0]}-${h.centroid[1]}-${h.display}`}
              role="option"
              aria-selected={false}
              tabIndex={0}
              onClick={() => pick(h)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault(); e.stopPropagation(); inputRef.current?.focus(); setOpen(false);
                } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  const options = Array.from(hostRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
                  const index = options.indexOf(e.currentTarget);
                  options[(index + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length]?.focus();
                } else if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  pick(h);
                }
              }}
              className="building-search-row"
            >
              <span className="building-search-name">{h.display}</span>
              {h.alt && <span className="building-search-alt mono">{h.alt}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
